import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Grid, Tabs, Tab,
  TextField, Select, MenuItem, FormControl, InputLabel, Pagination,
  CircularProgress, IconButton, Collapse, Paper, Divider, Breadcrumbs,
  LinearProgress, SelectChangeEvent, InputAdornment, Link, Tooltip,
  Dialog, DialogContent,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell, LineChart, Line, CartesianGrid,
} from 'recharts';

// ─── Types ───────────────────────────────────────────────────────────────────

interface EngagementData {
  post_engscore: number;
  engscore: number;
  likes: number;
  comments: number;
  brand: string;
  is_carousel: boolean;
  shortcode?: string;
  month?: string;
}

interface ImageResult {
  _image_name: string;
  description: string;
  primary_subject: string;
  setting: string;
  mood: string;
  labels: string[];
  colors_dominant: string[];
  content_type: string;
  people_count: number;
  has_text_overlay: boolean;
  text_content: string | null;
  brand_elements: string | null;
  visual_quality: string;
  composition: string;
  _prompt_tokens: number;
  _response_tokens: number;
  _total_tokens: number;
  _timestamp: string;
  _l1_categories?: string[];
  _label_taxonomy?: Array<{ label: string; l1: string; l2: string }>;
  _engagement?: EngagementData | null;
}

interface AnalysisData {
  stats: {
    total_processed: number;
    successful: number;
    errors: number;
    error_rate: number;
    estimated_cost_usd: number;
    total_input_tokens: number;
    total_output_tokens: number;
    avg_input_tokens: number;
    avg_output_tokens: number;
    content_type_distribution: Record<string, number>;
    setting_distribution: Record<string, number>;
    visual_quality_distribution: Record<string, number>;
    composition_distribution: Record<string, number>;
    total_unique_labels: number;
    avg_labels_per_image: number;
    top_50_labels: Record<string, number>;
    labels_above_5pct: Record<string, number>;
    top_moods: Record<string, number>;
    people_count_distribution: Record<string, number>;
    text_overlay_pct: number;
    brand_elements_pct: number;
    top_colors: Record<string, number>;
  };
  taxonomy: {
    category_summary: Record<string, {
      label_count: number;
      total_mentions: number;
      top_5: string[];
    }>;
    top_cooccurrences: Array<{ labels: string[]; count: number }>;
  };
  gcp_comparison?: {
    gemini_images: number;
    gcp_images: number;
    overlapping_images: number;
    gemini_unique_labels: number;
    gcp_unique_labels: number;
    gemini_avg_labels_per_image: number;
    gcp_avg_labels_per_image: number;
    gemini_top_20_labels: Record<string, number>;
    gcp_top_20_labels: Record<string, number>;
  };
}

interface EngagementMap {
  content_type: Record<string, number>;
  setting: Record<string, number>;
  visual_quality: Record<string, number>;
  composition: Record<string, number>;
  moods: Record<string, number>;
  people_count: Record<string, number>;
  colors: Record<string, number>;
  labels: Record<string, number>;
}

interface ResultsResponse {
  total: number;
  offset: number;
  limit: number;
  results: ImageResult[];
  engagement_stats?: {
    count: number;
    mean: number;
    median: number;
    max: number;
    total_images: number;
  } | null;
}

interface TaxonomyL1 {
  description: string;
  l2_subcategories: Record<string, string>;
}

interface TaxonomyData {
  taxonomy: Record<string, TaxonomyL1>;
  evaluation: {
    l1_distribution: Record<string, {
      label_count: number;
      image_instances: number;
      pct_of_labels: number;
      pct_of_instances: number;
    }>;
    l2_distribution: Record<string, number>;
  };
  meta: {
    total_labels: number;
    l1_count: number;
    l2_count: number;
  };
}

interface TaxonomyLabelEntry {
  label: string;
  image_count: number;
  avg_engagement: number | null;
}

interface TaxonomyLabelsResponse {
  grouped: Record<string, Record<string, TaxonomyLabelEntry[]>>;
  samples: Record<string, string[]>;
  l2_stats: Record<string, {
    image_count: number;
    avg_engagement: number | null;
    images_with_engagement: number;
  }>;
}

interface EngByCategoryEntry {
  name: string;
  image_count: number;
  avg_engagement: number | null;
  median_engagement: number | null;
  images_with_engagement: number;
}

interface BrandBreakdownEntry {
  name: string;
  image_count: number;
  avg_engagement: number;
  engagement_index: number;
  content_types: Record<string, number>;
  top_l1: Array<{ name: string; count: number }>;
  monthly: Array<{ month: string; avg_engagement: number; image_count: number }>;
}

interface BrandBreakdownResponse {
  brands: BrandBreakdownEntry[];
  overall_avg_engagement: number;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_SERVER_URL || 'https://127.0.0.1:5005';
const GCS_BASE = 'https://storage.googleapis.com/inklantern-media-data/Instagram_url';
const PAGE_SIZE = 24;
const ORG_ID = 178;

const CONTENT_TYPE_COLORS: Record<string, string> = {
  portrait: '#7c4dff', lifestyle: '#00bfa5', graphic_design: '#ff6d00',
  product: '#2979ff', landscape: '#00c853', meme: '#ffab00',
  group_photo: '#d50000', event: '#aa00ff', food: '#ff3d00',
  other: '#78909c', user_generated: '#1de9b6', behind_the_scenes: '#f50057',
  text_overlay: '#651fff', art: '#e040fb', automotive: '#42a5f5',
};

const QUALITY_COLORS: Record<string, string> = {
  professional: '#43a047', semi_professional: '#ffa000',
  casual: '#e53935', low_quality: '#757575',
};

// Engagement color scale
const engColor = (val: number, max: number) => {
  if (max === 0) return '#555';
  const pct = Math.min(val / max, 1);
  if (pct > 0.7) return '#00c853';
  if (pct > 0.4) return '#ffa000';
  if (pct > 0.15) return '#ff6d00';
  return '#78909c';
};

const formatEng = (val: number): string => {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
  return String(val);
};

// ─── Theme ───────────────────────────────────────────────────────────────────

const labTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#7c4dff' },
    secondary: { main: '#00e5ff' },
    background: { default: '#0a0a0f', paper: '#12121a' },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", Roboto, sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid rgba(124, 77, 255, 0.12)',
          '&:hover': { borderColor: 'rgba(124, 77, 255, 0.3)' },
          transition: 'border-color 0.2s',
        },
      },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
  },
});

// ─── Utility Components ──────────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: string | number; sub?: string; color?: string }> =
  ({ label, value, sub, color }) => (
  <Card sx={{ flex: 1, minWidth: 140 }}>
    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ color: color || 'primary.main', mt: 0.25 }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>}
    </CardContent>
  </Card>
);

const DistBar: React.FC<{ label: string; value: number; max: number; total: number; color?: string; avgEng?: number }> =
  ({ label, value, max, total, color, avgEng }) => {
  const pct = total > 0 ? (value / total * 100) : 0;
  return (
    <Box sx={{ mb: 0.75 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
        <Typography variant="caption" sx={{ fontWeight: 500, maxWidth: '50%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {avgEng !== undefined && (
            <Typography variant="caption" sx={{ color: '#00c853', fontSize: '0.6rem', fontWeight: 600 }}>
              {formatEng(avgEng)} avg
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {value} ({pct.toFixed(1)}%)
          </Typography>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={max > 0 ? (value / max * 100) : 0}
        sx={{
          height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.04)',
          '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: color || 'primary.main' },
        }}
      />
    </Box>
  );
};

const DistChart: React.FC<{ title: string; data: Record<string, number>; colors?: Record<string, string>; limit?: number; engData?: Record<string, number> }> =
  ({ title, data, colors, limit = 10, engData }) => {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, limit);
  const max = entries.length > 0 ? entries[0][1] : 0;
  const total = Object.values(data).reduce((s, v) => s + v, 0);
  return (
    <Card>
      <CardContent sx={{ py: 1.5, px: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
          {title}
        </Typography>
        {entries.map(([label, value]) => (
          <DistBar key={label} label={label} value={value} max={max} total={total} color={colors?.[label]} avgEng={engData?.[label]} />
        ))}
      </CardContent>
    </Card>
  );
};

// Thumbnail strip
const ThumbStrip: React.FC<{ images: string[]; size?: number }> = ({ images, size = 56 }) => (
  <Box sx={{ display: 'flex', gap: 0.75, overflow: 'hidden' }}>
    {images.map((imgName) => (
      <Box
        key={imgName}
        component="img"
        src={`${GCS_BASE}/${imgName}.jpeg`}
        sx={{
          width: size, height: size, borderRadius: 1, flexShrink: 0,
          objectFit: 'cover', border: '1px solid rgba(255,255,255,0.08)',
        }}
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
      />
    ))}
  </Box>
);

// Shared tooltip style — good contrast on dark bg
const tooltipStyle = {
  background: '#1e1e30',
  border: '1px solid rgba(124,77,255,0.35)',
  borderRadius: 8,
  fontSize: 12,
  color: '#e0e0e0',
};

// Engagement bar chart
const EngagementChart: React.FC<{
  data: Array<{ name: string; avg_engagement: number | null; image_count: number }>;
  onBarClick?: (name: string) => void;
  height?: number;
  title?: string;
  maxItems?: number;
  minImages?: number;
}> = ({ data, onBarClick, height = 260, title = 'Avg Engagement by Category', maxItems = 30, minImages = 1 }) => {
  const chartData = data
    .filter(d => d.avg_engagement !== null && d.image_count >= minImages)
    .map(d => ({ ...d, avg_engagement: d.avg_engagement || 0, displayName: d.name.replace(/_/g, ' ') }))
    .sort((a, b) => (b.avg_engagement || 0) - (a.avg_engagement || 0))
    .slice(0, maxItems);

  if (chartData.length === 0) return null;
  const maxEng = Math.max(...chartData.map(d => d.avg_engagement || 0));

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ py: 2, px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <TrendingUpIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
            {title}
          </Typography>
        </Box>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20, top: 0, bottom: 0 }}>
            <XAxis type="number" hide />
            <YAxis
              type="category"
              dataKey="displayName"
              width={160}
              tick={{ fill: '#ccc', fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <RechartsTooltip
              contentStyle={tooltipStyle}
              labelStyle={{ color: '#fff', fontWeight: 600 }}
              itemStyle={{ color: '#e0e0e0' }}
              formatter={(value: number) => [
                `${formatEng(value)} avg eng`,
                ''
              ]}
            />
            <Bar
              dataKey="avg_engagement"
              radius={[0, 4, 4, 0]}
              cursor={onBarClick ? 'pointer' : undefined}
              onClick={(_d: unknown, idx: number) => onBarClick && onBarClick(chartData[idx].name)}
            >
              {chartData.map((entry, idx) => (
                <Cell key={idx} fill={engColor(entry.avg_engagement || 0, maxEng)} fillOpacity={0.85} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// ─── Image Card with Engagement ─────────────────────────────────────────────

const ImageCard: React.FC<{
  result: ImageResult;
  onLabelClick: (label: string) => void;
  onL1Click?: (l1: string) => void;
  onL2Click?: (l1: string, l2: string) => void;
  onImageClick?: (result: ImageResult) => void;
  maxEng?: number;
}> = ({ result, onLabelClick, onL1Click, onL2Click, onImageClick, maxEng = 100000 }) => {
  const [expanded, setExpanded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const imgUrl = `${GCS_BASE}/${result._image_name}.jpeg`;
  const eng = result._engagement;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Image — click to open modal */}
      <Box
        sx={{ position: 'relative', pt: '100%', bgcolor: '#1a1a2e', overflow: 'hidden', borderBottom: '1px solid rgba(124,77,255,0.08)', cursor: onImageClick ? 'pointer' : undefined }}
        onClick={() => onImageClick?.(result)}
      >
        {!imgError ? (
          <Box component="img" src={imgUrl} alt={result.primary_subject} onError={() => setImgError(true)}
            sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary">Image unavailable</Typography>
          </Box>
        )}
        {/* Content type badge */}
        <Chip label={result.content_type} size="small"
          sx={{ position: 'absolute', top: 8, left: 8, bgcolor: CONTENT_TYPE_COLORS[result.content_type] || '#78909c', color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: 22 }}
        />
        {/* Engagement badge */}
        {eng && (
          <Tooltip title={`Post: ${formatEng(eng.post_engscore)} | Likes: ${formatEng(eng.likes)} | Comments: ${eng.comments}${eng.is_carousel ? ' | Carousel' : ''}`}>
            <Chip
              icon={<TrendingUpIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
              label={formatEng(eng.engscore)}
              size="small"
              sx={{
                position: 'absolute', bottom: 8, right: 8,
                bgcolor: engColor(eng.engscore, maxEng), color: '#fff',
                fontWeight: 700, fontSize: '0.7rem', height: 24,
              }}
            />
          </Tooltip>
        )}
        {/* Quality badge */}
        <Chip label={result.visual_quality} size="small"
          sx={{ position: 'absolute', top: 8, right: 8, bgcolor: QUALITY_COLORS[result.visual_quality] || '#757575', color: '#fff', fontSize: '0.65rem', height: 22 }}
        />
      </Box>

      {/* Content */}
      <CardContent sx={{ flex: 1, py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25, lineHeight: 1.3 }} noWrap>
          {result.primary_subject}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
          {eng?.brand && <><b>{eng.brand}</b> &middot; </>}
          {result.mood} &middot; {result.setting}
          {result.people_count > 0 && ` · ${result.people_count} ${result.people_count === 1 ? 'person' : 'people'}`}
        </Typography>

        <Typography variant="body2"
          sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5, mb: 0.75,
            display: '-webkit-box', WebkitLineClamp: expanded ? 99 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {result.description}
        </Typography>

        {/* Taxonomy chips */}
        {result._label_taxonomy && result._label_taxonomy.length > 0 && !expanded && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mb: 0.5 }}>
            {Array.from(new Set(result._l1_categories || [])).slice(0, 3).map(l1 => (
              <Chip key={l1} label={l1.replace(/_/g, ' ')} size="small"
                onClick={(e) => { e.stopPropagation(); onL1Click?.(l1); }}
                sx={{ height: 20, fontSize: '0.6rem', bgcolor: 'rgba(124,77,255,0.15)', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(124,77,255,0.3)' } }}
              />
            ))}
          </Box>
        )}

        {/* Labels */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
          {(expanded ? result.labels : result.labels.slice(0, 5)).map((label) => (
            <Chip key={label} label={label} size="small" variant="outlined"
              onClick={() => onLabelClick(label)}
              sx={{ height: 22, fontSize: '0.65rem', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(124,77,255,0.15)' } }}
            />
          ))}
          {!expanded && result.labels.length > 5 && (
            <Chip label={`+${result.labels.length - 5}`} size="small" sx={{ height: 22, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.06)' }} />
          )}
        </Box>

        {/* Expanded details */}
        <Collapse in={expanded}>
          <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.06)' }} />
          {result._label_taxonomy && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Taxonomy</Typography>
              {result._label_taxonomy.map(({ label: lbl, l1, l2 }) => (
                <Typography key={lbl} variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>
                  <Link component="span" onClick={() => onL1Click?.(l1)} sx={{ cursor: 'pointer', color: 'primary.main' }}>
                    {l1.replace(/_/g, ' ')}
                  </Link>
                  {' / '}
                  <Link component="span" onClick={() => onL2Click?.(l1, l2)} sx={{ cursor: 'pointer', color: 'secondary.main' }}>
                    {l2.replace(/_/g, ' ')}
                  </Link>
                  {' / '}{lbl}
                </Typography>
              ))}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.75 }}>
            <Typography variant="caption" sx={{ width: '100%', fontWeight: 600, mb: 0.25 }}>Colors</Typography>
            {result.colors_dominant.map((c) => (
              <Chip key={c} label={c} size="small" sx={{ height: 20, fontSize: '0.6rem' }} />
            ))}
          </Box>
          {result.text_content && (
            <Box sx={{ mb: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Text detected</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{result.text_content}</Typography>
            </Box>
          )}
          {result.brand_elements && result.brand_elements !== 'null' && (
            <Box sx={{ mb: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Brand elements</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{String(result.brand_elements)}</Typography>
            </Box>
          )}
          {eng && (
            <Box sx={{ mb: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Engagement</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Score: {formatEng(eng.engscore)} | Likes: {formatEng(eng.likes)} | Comments: {eng.comments}
                {eng.is_carousel && ' | Carousel (split)'}
              </Typography>
            </Box>
          )}
        </Collapse>

        <Box onClick={() => setExpanded(!expanded)}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', mt: 0.5, '&:hover': { color: 'primary.main' } }}
        >
          {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Image Detail Modal ─────────────────────────────────────────────────────

const ImageDetailModal: React.FC<{
  image: ImageResult | null;
  onClose: () => void;
  onLabelClick: (label: string) => void;
  onL1Click?: (l1: string) => void;
}> = ({ image, onClose, onLabelClick, onL1Click }) => {
  if (!image) return null;
  const eng = image._engagement;
  const postUrl = eng?.shortcode ? `https://www.instagram.com/p/${eng.shortcode}/` : null;

  return (
    <Dialog
      open={!!image}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { bgcolor: '#12121a', backgroundImage: 'none', borderRadius: 3, border: '1px solid rgba(124,77,255,0.2)' } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Close button */}
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
          <CloseIcon sx={{ color: '#fff', fontSize: 20 }} />
        </IconButton>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: 400 }}>
          {/* Left: image */}
          <Box sx={{ flex: '0 0 50%', maxWidth: { md: '50%' }, bgcolor: '#0a0a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box
              component="img"
              src={`${GCS_BASE}/${image._image_name}.jpeg`}
              alt={image.primary_subject}
              sx={{ width: '100%', maxHeight: 600, objectFit: 'contain' }}
            />
          </Box>

          {/* Right: details */}
          <Box sx={{ flex: 1, p: 3, overflowY: 'auto', maxHeight: 600 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{image.primary_subject}</Typography>
            {eng?.brand && (
              <Typography variant="body2" sx={{ color: '#bbb', mb: 1 }}>{eng.brand}</Typography>
            )}
            <Typography variant="body2" sx={{ color: '#ccc', mb: 2, lineHeight: 1.6 }}>{image.description}</Typography>

            {/* Engagement */}
            {eng && (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(0,200,83,0.06)', border: '1px solid rgba(0,200,83,0.15)' }}>
                <Typography variant="caption" sx={{ color: '#aaa', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
                  Engagement
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: '#00c853', lineHeight: 1 }}>{formatEng(eng.engscore)}</Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>{eng.is_carousel ? 'per image' : 'score'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ color: '#ccc', lineHeight: 1 }}>{formatEng(eng.likes)}</Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>likes</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ color: '#ccc', lineHeight: 1 }}>{eng.comments}</Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>comments</Typography>
                  </Box>
                  {eng.is_carousel && (
                    <Box>
                      <Typography variant="h6" sx={{ color: '#ff9800', lineHeight: 1 }}>{formatEng(eng.post_engscore)}</Typography>
                      <Typography variant="caption" sx={{ color: '#999' }}>full post</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Post link */}
            {postUrl && (
              <Box sx={{ mb: 2 }}>
                <Link href={postUrl} target="_blank" rel="noopener" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem' }}>
                  View on Instagram <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Link>
              </Box>
            )}

            {/* Meta */}
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
              <Chip label={image.content_type} size="small" sx={{ bgcolor: CONTENT_TYPE_COLORS[image.content_type] || '#78909c', color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: 22 }} />
              <Chip label={image.visual_quality} size="small" sx={{ bgcolor: QUALITY_COLORS[image.visual_quality] || '#757575', color: '#fff', fontSize: '0.65rem', height: 22 }} />
              <Chip label={image.mood} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 22 }} />
              <Chip label={image.setting} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 22 }} />
            </Box>

            {/* Taxonomy */}
            {image._label_taxonomy && image._label_taxonomy.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#aaa', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.08em', display: 'block', mb: 0.5 }}>
                  Categories
                </Typography>
                {image._label_taxonomy.map(({ label: lbl, l1, l2 }) => (
                  <Typography key={lbl} variant="caption" sx={{ color: '#bbb', display: 'block', fontSize: '0.75rem', mb: 0.25 }}>
                    <Link component="span" onClick={() => { onClose(); onL1Click?.(l1); }} sx={{ cursor: 'pointer', color: 'primary.main' }}>
                      {l1.replace(/_/g, ' ')}
                    </Link>
                    {' / '}{l2.replace(/_/g, ' ')}{' / '}{lbl}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Labels */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#aaa', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.08em', display: 'block', mb: 0.5 }}>
                Labels
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {image.labels.map(label => (
                  <Chip key={label} label={label} size="small" variant="outlined"
                    onClick={() => { onClose(); onLabelClick(label); }}
                    sx={{ height: 22, fontSize: '0.65rem', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(124,77,255,0.15)' } }}
                  />
                ))}
              </Box>
            </Box>

            {/* Colors */}
            <Box>
              <Typography variant="caption" sx={{ color: '#aaa', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.08em', display: 'block', mb: 0.5 }}>
                Colors
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {image.colors_dominant.map(c => (
                  <Chip key={c} label={c} size="small" sx={{ height: 20, fontSize: '0.6rem' }} />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const GeminiImageLab: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [analysis, setAnalysis] = useState<AnalysisData | null>(null);
  const [filteredAnalysis, setFilteredAnalysis] = useState<AnalysisData | null>(null);
  const [engagementMap, setEngagementMap] = useState<EngagementMap | null>(null);
  const [results, setResults] = useState<ImageResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [engStats, setEngStats] = useState<ResultsResponse['engagement_stats']>(null);
  const [loading, setLoading] = useState(true);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [contentTypeFilter, setContentTypeFilter] = useState('');
  const [settingFilter, setSettingFilter] = useState('');
  const [qualityFilter, setQualityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('');

  // Taxonomy state
  const [taxonomyData, setTaxonomyData] = useState<TaxonomyData | null>(null);
  const [taxLabelsData, setTaxLabelsData] = useState<TaxonomyLabelsResponse | null>(null);
  const [taxLabelsLoading, setTaxLabelsLoading] = useState(false);

  // Drill-down: l1 -> l2 -> label
  const [drillL1, setDrillL1] = useState<string | null>(null);
  const [drillL2, setDrillL2] = useState<string | null>(null);
  const [drillLabel, setDrillLabel] = useState<string | null>(null);

  // Engagement chart data
  const [engByCategory, setEngByCategory] = useState<EngByCategoryEntry[]>([]);
  const [engByMonth, setEngByMonth] = useState<EngByCategoryEntry[]>([]);
  const [brandFilter, setBrandFilter] = useState('');
  const [brandBreakdown, setBrandBreakdown] = useState<BrandBreakdownResponse | null>(null);
  const [exploreMode, setExploreMode] = useState<'categories' | 'brands'>('categories');
  const [hiddenBrands, setHiddenBrands] = useState<Set<string>>(new Set());

  // Month drill-down + image modal
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [monthPosts, setMonthPosts] = useState<ImageResult[]>([]);
  const [monthPostsLoading, setMonthPostsLoading] = useState(false);
  const [modalImage, setModalImage] = useState<ImageResult | null>(null);

  // Load analysis + taxonomy on mount
  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/api/gemini-lab/analysis?org_id=${ORG_ID}`).then(r => r.json()).catch(() => null),
      fetch(`${API_BASE}/api/gemini-lab/taxonomy?org_id=${ORG_ID}`).then(r => r.json()).catch(() => null),
    ]).then(([analysisData, taxData]) => {
      if (analysisData && !analysisData.error) setAnalysis(analysisData);
      if (taxData && taxData.taxonomy) setTaxonomyData(taxData);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Load results with filters
  const loadResults = useCallback(async () => {
    setResultsLoading(true);
    const params = new URLSearchParams({
      org_id: String(ORG_ID),
      offset: String((page - 1) * PAGE_SIZE),
      limit: String(PAGE_SIZE),
    });
    if (contentTypeFilter) params.set('content_type', contentTypeFilter);
    if (settingFilter) params.set('setting', settingFilter);
    if (qualityFilter) params.set('visual_quality', qualityFilter);
    if (brandFilter) params.set('brand', brandFilter);
    if (drillL1) params.set('l1_category', drillL1);
    if (drillL2) params.set('l2_category', drillL2);
    if (drillLabel) params.set('label', drillLabel);
    if (searchQuery) params.set('search', searchQuery);
    if (sortBy) params.set('sort_by', sortBy);

    try {
      const resp = await fetch(`${API_BASE}/api/gemini-lab/results?${params}`);
      const data: ResultsResponse = await resp.json();
      setResults(data.results);
      setTotalResults(data.total);
      setEngStats(data.engagement_stats || null);
    } catch {
      setResults([]);
      setTotalResults(0);
    }
    setResultsLoading(false);
  }, [page, contentTypeFilter, settingFilter, qualityFilter, brandFilter, drillL1, drillL2, drillLabel, searchQuery, sortBy]);

  // Load taxonomy labels when drilling
  const loadTaxonomyLabels = useCallback(async (l1: string | null, l2: string | null) => {
    if (!l1) return;
    setTaxLabelsLoading(true);
    const params = new URLSearchParams({ org_id: String(ORG_ID), l1 });
    if (l2) params.set('l2', l2);
    try {
      const resp = await fetch(`${API_BASE}/api/gemini-lab/taxonomy/labels?${params}`);
      const data: TaxonomyLabelsResponse = await resp.json();
      setTaxLabelsData(data);
    } catch {
      setTaxLabelsData(null);
    }
    setTaxLabelsLoading(false);
  }, []);

  // Load engagement by category for explore tab
  const loadEngByCategory = useCallback(async (groupBy: string) => {
    try {
      const resp = await fetch(`${API_BASE}/api/gemini-lab/engagement-by-category?org_id=${ORG_ID}&group_by=${groupBy}`);
      const data = await resp.json();
      setEngByCategory(data.categories || []);
    } catch {
      setEngByCategory([]);
    }
  }, []);

  // Load top posts for a selected month
  const loadMonthPosts = useCallback(async (month: string, excluded: Set<string>) => {
    setMonthPostsLoading(true);
    try {
      const params = new URLSearchParams({
        org_id: String(ORG_ID),
        month,
        sort_by: 'engagement_desc',
        limit: '12',
        offset: '0',
      });
      if (excluded.size > 0) params.set('exclude_brands', Array.from(excluded).join(','));
      const resp = await fetch(`${API_BASE}/api/gemini-lab/results?${params}`);
      const data: ResultsResponse = await resp.json();
      setMonthPosts(data.results);
    } catch {
      setMonthPosts([]);
    }
    setMonthPostsLoading(false);
  }, []);

  // Fetch filtered analysis (with engagement) on mount and when brands change
  useEffect(() => {
    const params = new URLSearchParams({ org_id: String(ORG_ID) });
    if (hiddenBrands.size > 0) params.set('exclude_brands', Array.from(hiddenBrands).join(','));
    fetch(`${API_BASE}/api/gemini-lab/analysis/filtered?${params}`)
      .then(r => r.json())
      .then(data => {
        if (data.stats) {
          if (hiddenBrands.size > 0) setFilteredAnalysis(data as AnalysisData);
          else setFilteredAnalysis(null);
        }
        if (data.engagement) setEngagementMap(data.engagement as EngagementMap);
      })
      .catch(() => {});
  }, [hiddenBrands]);

  // Re-fetch month posts when hidden brands change
  useEffect(() => {
    if (selectedMonth) loadMonthPosts(selectedMonth, hiddenBrands);
  }, [hiddenBrands, selectedMonth, loadMonthPosts]);

  // Load results when on explore tab
  useEffect(() => {
    if (tab === 0) loadResults();
  }, [tab, loadResults]);

  // Load taxonomy labels when drilling into L1 or L2
  useEffect(() => {
    if (tab === 0 && drillL1) {
      loadTaxonomyLabels(drillL1, drillL2);
    }
  }, [tab, drillL1, drillL2, loadTaxonomyLabels]);

  // Load engagement chart when entering explore
  useEffect(() => {
    if (tab === 0 && !drillL1) {
      loadEngByCategory('l1');
    } else if (tab === 0 && drillL1 && !drillL2) {
      // Load engagement by L2 within this L1 - we have it in taxLabelsData.l2_stats
    }
  }, [tab, drillL1, drillL2, loadEngByCategory]);

  // Load brand breakdown + month data on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/gemini-lab/brand-breakdown?org_id=${ORG_ID}`)
      .then(r => r.json()).then((d: BrandBreakdownResponse) => setBrandBreakdown(d)).catch(() => {});
    fetch(`${API_BASE}/api/gemini-lab/engagement-by-category?org_id=${ORG_ID}&group_by=month`)
      .then(r => r.json()).then(d => setEngByMonth(d.categories || [])).catch(() => {});
  }, []);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [contentTypeFilter, settingFilter, qualityFilter, brandFilter, drillL1, drillL2, drillLabel, searchQuery, sortBy]);

  const handleSearch = () => setSearchQuery(searchInput);

  const clearFilters = () => {
    setContentTypeFilter('');
    setSettingFilter('');
    setQualityFilter('');
    setBrandFilter('');
    setSearchQuery('');
    setSearchInput('');
    setSortBy('');
  };

  const handleLabelClick = (label: string) => {
    setDrillLabel(label);
    clearFilters();
    setTab(0);
  };

  const handleL1Click = (l1: string) => {
    setDrillL1(l1);
    setDrillL2(null);
    setDrillLabel(null);
    clearFilters();
    setTab(0);
  };

  const handleL2Click = (l1: string, l2: string) => {
    setDrillL1(l1);
    setDrillL2(l2);
    setDrillLabel(null);
    clearFilters();
    setTab(0);
  };

  const resetDrill = () => {
    setDrillL1(null);
    setDrillL2(null);
    setDrillLabel(null);
    clearFilters();
    setExploreMode('categories');
  };

  const contentTypes = useMemo(() =>
    analysis ? Object.keys(analysis.stats.content_type_distribution).sort() : [], [analysis]);
  const settings = useMemo(() =>
    analysis ? Object.keys(analysis.stats.setting_distribution).sort() : [], [analysis]);
  const qualities = useMemo(() =>
    analysis ? Object.keys(analysis.stats.visual_quality_distribution).sort() : [], [analysis]);
  const brands = useMemo(() =>
    brandBreakdown ? brandBreakdown.brands.map(b => b.name).sort() : [], [brandBreakdown]);

  const maxEngInResults = useMemo(() => {
    const vals = results.filter(r => r._engagement).map(r => r._engagement!.engscore);
    return vals.length > 0 ? Math.max(...vals) : 100000;
  }, [results]);

  if (loading) {
    return (
      <ThemeProvider theme={labTheme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={labTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Header */}
        <Box sx={{
          px: { xs: 2, md: 4 }, py: 3,
          background: 'linear-gradient(135deg, rgba(124,77,255,0.12) 0%, rgba(0,229,255,0.06) 100%)',
          borderBottom: '1px solid rgba(124,77,255,0.15)',
        }}>
          <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Typography variant="h4">Gemini Image Lab</Typography>
              <Chip label="org 178" size="small" sx={{ bgcolor: 'rgba(0,229,255,0.2)', color: '#00e5ff', fontWeight: 600, fontSize: '0.7rem' }} />
              <Chip label="experimental" size="small" sx={{ bgcolor: 'rgba(124,77,255,0.2)', color: '#b388ff', fontWeight: 600, fontSize: '0.7rem' }} />
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
              Gemini 2.0 Flash image analysis on {analysis?.stats.successful.toLocaleString() || '...'} Instagram images with engagement data
            </Typography>

            {analysis && (
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <StatCard label="Images" value={analysis.stats.successful.toLocaleString()} />
                <StatCard label="Labels" value={analysis.stats.total_unique_labels.toLocaleString()} sub={`${analysis.stats.avg_labels_per_image.toFixed(1)} avg/image`} color="#00e5ff" />
                {taxonomyData && (
                  <StatCard label="Taxonomy" value={`${taxonomyData.meta.l1_count} / ${taxonomyData.meta.l2_count}`} sub="L1 / L2 categories" color="#ff9800" />
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ px: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
          <Tabs value={tab} onChange={(_, v) => { setTab(v); if (v === 0) resetDrill(); }}
            sx={{ mt: 2, mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9rem' } }}
          >
            <Tab label="Explore" />
            <Tab label="Overview" />
          </Tabs>

          {/* ─── Tab 1: Overview ─── */}
          {tab === 1 && analysis && (() => {
            const toggleBrand = (name: string) => {
              setHiddenBrands(prev => {
                const next = new Set(prev);
                if (next.has(name)) next.delete(name); else next.add(name);
                return next;
              });
            };
            const visibleBrands = brandBreakdown ? brandBreakdown.brands.filter(b => !hiddenBrands.has(b.name)) : [];
            const brandsFiltered = hiddenBrands.size > 0;
            const active = (brandsFiltered && filteredAnalysis) ? filteredAnalysis : analysis;

            return (
            <Box sx={{ pb: 4 }}>
              {/* Brand filter chips — top of page, affects all components */}
              {brandBreakdown && brandBreakdown.brands.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent sx={{ py: 2, px: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                      Filter by Brand
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 1.5 }}>
                      Click to toggle. Affects all charts and data on this page.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {brandBreakdown.brands.map(b => {
                        const isHidden = hiddenBrands.has(b.name);
                        return (
                          <Chip
                            key={b.name}
                            label={`${b.name} (${b.image_count})`}
                            size="small"
                            onClick={() => toggleBrand(b.name)}
                            sx={{
                              height: 26, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                              bgcolor: isHidden ? 'rgba(255,255,255,0.04)' : 'rgba(124,77,255,0.12)',
                              color: isHidden ? '#555' : '#ddd',
                              border: isHidden ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(124,77,255,0.3)',
                              textDecoration: isHidden ? 'line-through' : 'none',
                              opacity: isHidden ? 0.45 : 1,
                              '&:hover': { bgcolor: isHidden ? 'rgba(255,255,255,0.08)' : 'rgba(124,77,255,0.22)' },
                              transition: 'all 0.15s',
                            }}
                          />
                        );
                      })}
                      {hiddenBrands.size > 0 && (
                        <Chip
                          label="Show All"
                          size="small"
                          onClick={() => setHiddenBrands(new Set())}
                          sx={{ height: 26, fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer', bgcolor: 'rgba(124,77,255,0.15)', color: '#b388ff', '&:hover': { bgcolor: 'rgba(124,77,255,0.25)' } }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <DistChart title="Content Type" data={active.stats.content_type_distribution} colors={CONTENT_TYPE_COLORS} engData={engagementMap?.content_type} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DistChart title="Setting" data={active.stats.setting_distribution} engData={engagementMap?.setting} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DistChart title="Visual Quality" data={active.stats.visual_quality_distribution} colors={QUALITY_COLORS} engData={engagementMap?.visual_quality} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DistChart title="Composition" data={active.stats.composition_distribution} engData={engagementMap?.composition} />
                </Grid>
              </Grid>

              {/* Avg Engagement by Brand */}
              {visibleBrands.length > 0 && (
                <EngagementChart
                  data={visibleBrands.map(b => ({
                    name: `${b.name} (${b.image_count})`,
                    image_count: b.image_count,
                    avg_engagement: b.avg_engagement,
                  }))}
                  title="Avg Engagement by Brand"
                  height={Math.min(visibleBrands.length * 28 + 40, 500)}
                  maxItems={50}
                  minImages={1}
                />
              )}

              {/* Engagement by Month — per-brand lines, filtered by hiddenBrands */}
              {brandBreakdown && brandBreakdown.brands.length > 0 && (() => {
                const BRAND_COLORS = ['#7c4dff', '#00e5ff', '#ff9800', '#00c853', '#ff5252', '#ffeb3b', '#e040fb', '#42a5f5', '#ff6d00', '#1de9b6'];
                const visibleBrands = brandBreakdown.brands
                  .filter(b => !hiddenBrands.has(b.name) && b.monthly.length > 2)
                  .slice(0, 10);
                if (visibleBrands.length === 0) return null;
                // Build unified month list
                const allMonths = new Set<string>();
                visibleBrands.forEach(b => b.monthly.forEach(m => allMonths.add(m.month)));
                const months = Array.from(allMonths).sort();
                const chartData = months.map(month => {
                  const row: Record<string, string | number> = { month };
                  visibleBrands.forEach(b => {
                    const md = b.monthly.find(m => m.month === month);
                    row[b.name] = md ? md.avg_engagement : 0;
                  });
                  return row;
                });
                return (
                  <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ py: 2, px: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                        Monthly Engagement Trend by Brand
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 1 }}>
                        Click a month to see top posts
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart
                          data={chartData}
                          margin={{ top: 5, right: 20, bottom: 5, left: 10 }}
                          onClick={(e: { activeLabel?: string } | null) => {
                            if (e?.activeLabel) {
                              const month = e.activeLabel;
                              setSelectedMonth(prev => prev === month ? null : month);
                              if (month !== selectedMonth) loadMonthPosts(month, hiddenBrands);
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                          <XAxis
                            dataKey="month"
                            tick={{ fill: '#ccc', fontSize: 11 }}
                            tickFormatter={(v: string) => {
                              const [y, m] = v.split('-');
                              return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m)-1]} '${y.slice(2)}`;
                            }}
                          />
                          <YAxis tick={{ fill: '#ccc', fontSize: 11 }} tickFormatter={(v: number) => formatEng(v)} />
                          <RechartsTooltip
                            contentStyle={tooltipStyle}
                            labelStyle={{ color: '#fff', fontWeight: 600 }}
                            itemStyle={{ color: '#e0e0e0' }}
                            formatter={(value: number, name: string) => [formatEng(value), name]}
                            labelFormatter={(label: string) => {
                              const [y, m] = label.split('-');
                              return `${['January','February','March','April','May','June','July','August','September','October','November','December'][parseInt(m)-1]} ${y}`;
                            }}
                          />
                          {visibleBrands.map((b, i) => (
                            <Line key={b.name} type="monotone" dataKey={b.name} stroke={BRAND_COLORS[i % BRAND_COLORS.length]}
                              strokeWidth={2} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} connectNulls />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                      <Box sx={{ display: 'flex', gap: 2, mt: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {visibleBrands.map((b, i) => (
                          <Box key={b.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 12, height: 3, bgcolor: BRAND_COLORS[i % BRAND_COLORS.length], borderRadius: 1 }} />
                            <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.7rem' }}>{b.name}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Top posts for selected month */}
              {selectedMonth && (
                <Card sx={{ mb: 3 }}>
                  <CardContent sx={{ py: 2, px: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                          Top Posts — {(() => {
                            const [y, m] = selectedMonth.split('-');
                            return `${['January','February','March','April','May','June','July','August','September','October','November','December'][parseInt(m)-1]} ${y}`;
                          })()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999' }}>
                          Sorted by engagement (highest first)
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => setSelectedMonth(null)} sx={{ color: '#999' }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                    {monthPostsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                    ) : monthPosts.length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>No posts found for this month.</Typography>
                    ) : (
                      <Grid container spacing={2}>
                        {monthPosts.map((r) => (
                          <Grid item xs={6} sm={4} md={3} lg={2} key={r._image_name}>
                            <ImageCard result={r} onLabelClick={handleLabelClick} onL1Click={handleL1Click} onL2Click={handleL2Click} onImageClick={setModalImage} maxEng={Math.max(...monthPosts.filter(p => p._engagement).map(p => p._engagement!.engscore), 1)} />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              )}

              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ py: 2, px: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                    Top 30 Labels (click to explore)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {Object.entries(active.stats.top_50_labels).slice(0, 30).map(([label, count]) => {
                      const labelEng = engagementMap?.labels?.[label];
                      return (
                        <Tooltip key={label} title={labelEng ? `${formatEng(labelEng)} avg engagement` : ''} placement="top">
                          <Chip
                            label={labelEng ? `${label} (${count}) · ${formatEng(labelEng)}` : `${label} (${count})`}
                            size="small"
                            onClick={() => handleLabelClick(label)}
                            sx={{ cursor: 'pointer', bgcolor: 'rgba(124,77,255,0.1)', '&:hover': { bgcolor: 'rgba(124,77,255,0.25)' } }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <DistChart title="Dominant Colors" data={active.stats.top_colors} limit={10} engData={engagementMap?.colors} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DistChart title="Moods" data={active.stats.top_moods} limit={10} engData={engagementMap?.moods} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DistChart title="People Count" data={active.stats.people_count_distribution} limit={8} engData={engagementMap?.people_count} />
                </Grid>
              </Grid>
            </Box>
            );
          })()}

          {/* ─── Tab 1: Explore (Combined Browse + Taxonomy) ─── */}
          {/* ─── Tab 0: Explore ─── */}
          {tab === 0 && (
            <Box sx={{ pb: 4 }}>
              {/* Breadcrumb navigation */}
              <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: 16 }} />} sx={{ mb: 2 }}>
                <Link component="span" onClick={resetDrill}
                  sx={{ cursor: 'pointer', fontWeight: 700, color: !drillL1 ? 'text.primary' : 'primary.main', fontSize: '0.9rem' }}
                >
                  {brandFilter ? `${brandFilter} — Categories` : 'All Categories'}
                </Link>
                {drillL1 && (
                  <Link component="span" onClick={() => { setDrillL2(null); setDrillLabel(null); }}
                    sx={{ cursor: 'pointer', fontWeight: 700, color: !drillL2 ? 'text.primary' : 'primary.main', fontSize: '0.9rem', textTransform: 'capitalize' }}
                  >
                    {drillL1.replace(/_/g, ' ')}
                  </Link>
                )}
                {drillL2 && (
                  <Link component="span" onClick={() => { setDrillLabel(null); }}
                    sx={{ cursor: 'pointer', fontWeight: 700, color: !drillLabel ? 'text.primary' : 'primary.main', fontSize: '0.9rem', textTransform: 'capitalize' }}
                  >
                    {drillL2.replace(/_/g, ' ')}
                  </Link>
                )}
                {drillLabel && (
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{drillLabel}</Typography>
                )}
              </Breadcrumbs>

              {/* ── Level 0: All categories overview ── */}
              {!drillL1 && !drillLabel && taxonomyData && (
                <>
                  {/* Filters + search */}
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField size="small" placeholder="Search descriptions, labels..."
                      value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={handleSearch}><SearchIcon /></IconButton></InputAdornment> }}
                      sx={{ minWidth: 280, flex: 1 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Brand</InputLabel>
                      <Select value={brandFilter} label="Brand" onChange={(e: SelectChangeEvent) => setBrandFilter(e.target.value)}>
                        <MenuItem value="">All Brands</MenuItem>
                        {brands.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Content Type</InputLabel>
                      <Select value={contentTypeFilter} label="Content Type" onChange={(e: SelectChangeEvent) => setContentTypeFilter(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        {contentTypes.map(ct => <MenuItem key={ct} value={ct}>{ct}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                      <InputLabel>Sort</InputLabel>
                      <Select value={sortBy} label="Sort" onChange={(e: SelectChangeEvent) => setSortBy(e.target.value)}>
                        <MenuItem value="">Default</MenuItem>
                        <MenuItem value="engagement_desc">Engagement (high)</MenuItem>
                        <MenuItem value="engagement_asc">Engagement (low)</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* If search is active, show results */}
                  {searchQuery ? (
                    <>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        {totalResults} results for "{searchQuery}"
                        {engStats && ` · Avg engagement: ${formatEng(engStats.mean)}`}
                      </Typography>
                      {resultsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                      ) : (
                        <>
                          <Grid container spacing={2}>
                            {results.map((r) => (
                              <Grid item xs={12} sm={6} md={4} lg={3} key={r._image_name}>
                                <ImageCard result={r} onLabelClick={handleLabelClick} onL1Click={handleL1Click} onL2Click={handleL2Click} onImageClick={setModalImage} maxEng={maxEngInResults} />
                              </Grid>
                            ))}
                          </Grid>
                          {totalResults > PAGE_SIZE && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                              <Pagination count={Math.ceil(totalResults / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
                            </Box>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Mode toggle: Categories vs Brands */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Chip label="By Category" onClick={() => setExploreMode('categories')}
                          sx={{ fontWeight: 600, bgcolor: exploreMode === 'categories' ? 'rgba(124,77,255,0.25)' : 'rgba(255,255,255,0.05)',
                            border: exploreMode === 'categories' ? '1px solid rgba(124,77,255,0.5)' : '1px solid transparent',
                            cursor: 'pointer', '&:hover': { bgcolor: 'rgba(124,77,255,0.15)' } }}
                        />
                        <Chip label="By Brand" onClick={() => setExploreMode('brands')}
                          sx={{ fontWeight: 600, bgcolor: exploreMode === 'brands' ? 'rgba(0,229,255,0.2)' : 'rgba(255,255,255,0.05)',
                            border: exploreMode === 'brands' ? '1px solid rgba(0,229,255,0.4)' : '1px solid transparent',
                            cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,229,255,0.1)' } }}
                        />
                      </Box>

                      {exploreMode === 'categories' && (
                        <>
                          {/* Engagement chart */}
                          {engByCategory.length > 0 && (
                            <EngagementChart data={engByCategory} onBarClick={(name) => handleL1Click(name)} />
                          )}

                          {/* L1 category cards */}
                          <Grid container spacing={2}>
                            {Object.entries(taxonomyData.taxonomy)
                              .sort((a, b) => {
                                const aInst = taxonomyData.evaluation.l1_distribution[a[0]]?.image_instances || 0;
                                const bInst = taxonomyData.evaluation.l1_distribution[b[0]]?.image_instances || 0;
                                return bInst - aInst;
                              })
                              .map(([l1Name, l1Data]) => {
                                const dist = taxonomyData.evaluation.l1_distribution[l1Name];
                                const l2Entries = Object.entries(l1Data.l2_subcategories);
                                const engEntry = engByCategory.find(e => e.name === l1Name);
                                const avgEng = engEntry?.avg_engagement;
                                const maxAvgEng = Math.max(...engByCategory.map(e => e.avg_engagement || 0));
                                return (
                                  <Grid item xs={12} sm={6} md={4} lg={3} key={l1Name}>
                                    <Card onClick={() => handleL1Click(l1Name)}
                                      sx={{ cursor: 'pointer', '&:hover': { borderColor: 'rgba(124,77,255,0.5)', transform: 'translateY(-2px)' }, transition: 'all 0.2s' }}
                                    >
                                      <CardContent sx={{ py: 2, px: 2.5 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, textTransform: 'capitalize', mb: 0.5 }}>
                                          {l1Name.replace(/_/g, ' ')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1, lineHeight: 1.4 }}>
                                          {l1Data.description}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                                          <Chip label={`${dist?.image_instances || 0} images`} size="small" sx={{ height: 20, fontSize: '0.6rem', bgcolor: 'rgba(124,77,255,0.12)' }} />
                                          <Chip label={`${dist?.label_count || 0} labels`} size="small" sx={{ height: 20, fontSize: '0.6rem', bgcolor: 'rgba(0,229,255,0.08)' }} />
                                          {avgEng != null && (
                                            <Chip
                                              icon={<TrendingUpIcon sx={{ fontSize: 12 }} />}
                                              label={`${formatEng(avgEng)} avg`}
                                              size="small"
                                              sx={{ height: 20, fontSize: '0.6rem', bgcolor: `${engColor(avgEng, maxAvgEng)}22`, color: engColor(avgEng, maxAvgEng), '& .MuiChip-icon': { color: 'inherit' } }}
                                            />
                                          )}
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                                          {l2Entries.map(([l2Name], i) => (
                                            <span key={l2Name}>{l2Name.replace(/_/g, ' ')}{i < l2Entries.length - 1 ? ', ' : ''}</span>
                                          ))}
                                        </Typography>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                );
                              })}
                          </Grid>
                        </>
                      )}

                      {exploreMode === 'brands' && brandBreakdown && (
                        <>
                          {/* Brand avg engagement chart */}
                          <EngagementChart
                            data={brandBreakdown.brands.map(b => ({
                              name: b.name,
                              image_count: b.image_count,
                              avg_engagement: b.avg_engagement,
                            }))}
                            onBarClick={(name) => { setBrandFilter(name); setExploreMode('categories'); }}
                            title="Avg Engagement by Brand"
                            height={Math.min(brandBreakdown.brands.length * 28 + 40, 500)}
                            maxItems={50}
                            minImages={1}
                          />
                          <Typography variant="caption" sx={{ color: '#bbb', display: 'block', mb: 2, mt: -1 }}>
                            Dataset average: {formatEng(brandBreakdown.overall_avg_engagement)}. Click a brand to explore its content.
                          </Typography>

                          {/* Brand cards */}
                          <Grid container spacing={2}>
                            {brandBreakdown.brands.map(brand => {
                              const topCt = Object.entries(brand.content_types).sort((a, b) => b[1] - a[1]).slice(0, 3);
                              return (
                                <Grid item xs={12} sm={6} md={4} key={brand.name}>
                                  <Card
                                    onClick={() => { setBrandFilter(brand.name); setExploreMode('categories'); }}
                                    sx={{ cursor: 'pointer', '&:hover': { borderColor: 'rgba(0,229,255,0.5)', transform: 'translateY(-2px)' }, transition: 'all 0.2s' }}
                                  >
                                    <CardContent sx={{ py: 2, px: 2.5 }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{brand.name}</Typography>
                                        <Chip
                                          label={formatEng(brand.avg_engagement)}
                                          icon={<TrendingUpIcon sx={{ fontSize: 12, color: '#00c853 !important' }} />}
                                          size="small"
                                          sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22, bgcolor: 'rgba(0,200,83,0.12)', color: '#00c853' }}
                                        />
                                      </Box>
                                      <Typography variant="caption" sx={{ color: '#bbb', display: 'block', mb: 1 }}>
                                        {brand.image_count} images · {formatEng(brand.avg_engagement)} avg engagement
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                        {topCt.map(([ct, count]) => (
                                          <Chip key={ct} label={`${ct} (${count})`} size="small"
                                            sx={{ height: 18, fontSize: '0.55rem', bgcolor: 'rgba(124,77,255,0.1)' }} />
                                        ))}
                                      </Box>
                                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {brand.top_l1.slice(0, 3).map(l1 => (
                                          <Chip key={l1.name} label={l1.name.replace(/_/g, ' ')} size="small"
                                            sx={{ height: 18, fontSize: '0.55rem', bgcolor: 'rgba(0,229,255,0.08)', textTransform: 'capitalize' }} />
                                        ))}
                                      </Box>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                </>
              )}

              {/* ── Level 1: L2 subcategories within selected L1 ── */}
              {drillL1 && !drillL2 && !drillLabel && taxonomyData && (
                <>
                  {(() => {
                    const l1Data = taxonomyData.taxonomy[drillL1];
                    if (!l1Data) return null;

                    // Build engagement chart from l2_stats
                    const l2ChartData = taxLabelsData ? Object.entries(taxLabelsData.l2_stats || {}).map(([name, stats]) => ({
                      name,
                      avg_engagement: stats.avg_engagement,
                      image_count: stats.image_count,
                    })) : [];

                    return (
                      <>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>{l1Data.description}</Typography>

                        {/* L2 engagement chart */}
                        {l2ChartData.length > 0 && (
                          <EngagementChart data={l2ChartData} onBarClick={(name) => handleL2Click(drillL1, name)} height={200} />
                        )}

                        {taxLabelsLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                        ) : (
                          <Grid container spacing={2}>
                            {Object.entries(l1Data.l2_subcategories)
                              .sort((a, b) => {
                                const aC = taxonomyData.evaluation.l2_distribution[`${drillL1}/${a[0]}`] || 0;
                                const bC = taxonomyData.evaluation.l2_distribution[`${drillL1}/${b[0]}`] || 0;
                                return bC - aC;
                              })
                              .map(([l2Name, l2Desc]) => {
                                const sampleImages = taxLabelsData?.samples?.[l2Name] || [];
                                const labelsInL2 = taxLabelsData?.grouped?.[drillL1]?.[l2Name] || [];
                                const l2Stat = taxLabelsData?.l2_stats?.[l2Name];
                                const topLabels = labelsInL2.slice(0, 6);
                                return (
                                  <Grid item xs={12} md={6} key={l2Name}>
                                    <Card onClick={() => handleL2Click(drillL1, l2Name)}
                                      sx={{ cursor: 'pointer', '&:hover': { borderColor: 'rgba(124,77,255,0.5)', transform: 'translateY(-1px)' }, transition: 'all 0.2s' }}
                                    >
                                      <CardContent sx={{ py: 2, px: 2.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                          <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                                              {l2Name.replace(/_/g, ' ')}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{l2Desc}</Typography>
                                          </Box>
                                          <Box sx={{ textAlign: 'right', ml: 2, flexShrink: 0 }}>
                                            <Typography variant="h6" sx={{ color: 'primary.main', lineHeight: 1 }}>{labelsInL2.length}</Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>labels</Typography>
                                            {l2Stat?.avg_engagement && (
                                              <Typography variant="caption" sx={{ color: '#00c853', display: 'block' }}>
                                                {formatEng(l2Stat.avg_engagement)} avg
                                              </Typography>
                                            )}
                                          </Box>
                                        </Box>
                                        {sampleImages.length > 0 && (
                                          <Box sx={{ mb: 1.5 }}>
                                            <ThumbStrip images={sampleImages} />
                                          </Box>
                                        )}
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                          {topLabels.map(({ label: lbl, image_count }) => (
                                            <Chip key={lbl} label={`${lbl} (${image_count})`} size="small" variant="outlined"
                                              sx={{ height: 22, fontSize: '0.6rem' }}
                                            />
                                          ))}
                                          {labelsInL2.length > 6 && (
                                            <Chip label={`+${labelsInL2.length - 6}`} size="small" sx={{ height: 22, fontSize: '0.6rem', bgcolor: 'rgba(255,255,255,0.04)' }} />
                                          )}
                                        </Box>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                );
                              })}
                          </Grid>
                        )}

                        {/* Also show images filtered to this L1 */}
                        <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.06)' }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                            Images in {drillL1.replace(/_/g, ' ')}
                          </Typography>
                          <FormControl size="small" sx={{ minWidth: 110 }}>
                            <InputLabel>Sort</InputLabel>
                            <Select value={sortBy} label="Sort" onChange={(e: SelectChangeEvent) => setSortBy(e.target.value)}>
                              <MenuItem value="">Default</MenuItem>
                              <MenuItem value="engagement_desc">Engagement (high)</MenuItem>
                              <MenuItem value="engagement_asc">Engagement (low)</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        {resultsLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                        ) : (
                          <>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                              {totalResults} images{engStats ? ` · Avg engagement: ${formatEng(engStats.mean)} · Median: ${formatEng(engStats.median)}` : ''}
                            </Typography>
                            <Grid container spacing={2}>
                              {results.slice(0, 8).map((r) => (
                                <Grid item xs={6} sm={4} md={3} key={r._image_name}>
                                  <ImageCard result={r} onLabelClick={handleLabelClick} onL1Click={handleL1Click} onL2Click={handleL2Click} onImageClick={setModalImage} maxEng={maxEngInResults} />
                                </Grid>
                              ))}
                            </Grid>
                            {totalResults > 8 && (
                              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Pagination count={Math.ceil(totalResults / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} color="primary" size="small" />
                              </Box>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                </>
              )}

              {/* ── Level 2: Labels within selected L2, plus images ── */}
              {drillL1 && drillL2 && !drillLabel && (
                <>
                  {(() => {
                    const l2Desc = taxonomyData?.taxonomy[drillL1]?.l2_subcategories?.[drillL2] || '';
                    const labelsInL2 = taxLabelsData?.grouped?.[drillL1]?.[drillL2] || [];
                    const sampleImages = taxLabelsData?.samples?.[drillL2] || [];

                    // Build bar chart data from labels with engagement
                    const labelChartData = labelsInL2
                      .filter(l => l.avg_engagement !== null && l.image_count >= 2)
                      .map(l => ({ name: l.label, avg_engagement: l.avg_engagement, image_count: l.image_count }));

                    return (
                      <>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>{l2Desc}</Typography>

                        {sampleImages.length > 0 && (
                          <Box sx={{ mb: 2 }}><ThumbStrip images={sampleImages} size={72} /></Box>
                        )}

                        {/* Label engagement chart */}
                        {labelChartData.length > 0 && (
                          <EngagementChart data={labelChartData} onBarClick={(name) => { setDrillLabel(name); }} height={Math.min(labelChartData.length * 28 + 40, 400)} />
                        )}

                        {/* Labels table (top 30) */}
                        <Card sx={{ mb: 3 }}>
                          <CardContent sx={{ py: 1.5, px: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', px: 1, mb: 0.5 }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                                Top {Math.min(30, labelsInL2.filter(l => l.image_count > 0).length)} of {labelsInL2.filter(l => l.image_count > 0).length} labels
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', px: 1, py: 0.75, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, flex: 1, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6rem' }}>Label</Typography>
                              <Typography variant="caption" sx={{ fontWeight: 700, width: 80, textAlign: 'right', textTransform: 'uppercase', fontSize: '0.6rem' }}>Images</Typography>
                              <Typography variant="caption" sx={{ fontWeight: 700, width: 90, textAlign: 'right', textTransform: 'uppercase', fontSize: '0.6rem' }}>Avg Eng</Typography>
                              <Box sx={{ width: 60 }} />
                            </Box>
                            {labelsInL2.filter(l => l.image_count > 0).slice(0, 30).map(({ label: lbl, image_count, avg_engagement }) => (
                              <Box key={lbl}
                                sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'rgba(124,77,255,0.06)' } }}
                              >
                                <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, fontSize: '0.85rem' }}>{lbl}</Typography>
                                <Typography variant="body2" sx={{ width: 80, textAlign: 'right', color: 'text.secondary' }}>{image_count}</Typography>
                                <Typography variant="body2" sx={{ width: 90, textAlign: 'right', color: avg_engagement ? '#00c853' : 'text.secondary' }}>
                                  {avg_engagement ? formatEng(avg_engagement) : '—'}
                                </Typography>
                                <Box sx={{ width: 60, display: 'flex', justifyContent: 'flex-end' }}>
                                  <Chip label="view" size="small" onClick={() => setDrillLabel(lbl)}
                                    sx={{ height: 22, fontSize: '0.6rem', cursor: 'pointer', bgcolor: 'rgba(124,77,255,0.12)', '&:hover': { bgcolor: 'rgba(124,77,255,0.25)' } }}
                                  />
                                </Box>
                              </Box>
                            ))}
                          </CardContent>
                        </Card>

                        {/* Images in this L2 */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {totalResults} images{engStats ? ` · Avg: ${formatEng(engStats.mean)}` : ''}
                          </Typography>
                          <FormControl size="small" sx={{ minWidth: 110 }}>
                            <InputLabel>Sort</InputLabel>
                            <Select value={sortBy} label="Sort" onChange={(e: SelectChangeEvent) => setSortBy(e.target.value)}>
                              <MenuItem value="">Default</MenuItem>
                              <MenuItem value="engagement_desc">Engagement (high)</MenuItem>
                              <MenuItem value="engagement_asc">Engagement (low)</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        {resultsLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                        ) : (
                          <>
                            <Grid container spacing={2}>
                              {results.slice(0, 12).map((r) => (
                                <Grid item xs={6} sm={4} md={3} key={r._image_name}>
                                  <ImageCard result={r} onLabelClick={handleLabelClick} onL1Click={handleL1Click} onL2Click={handleL2Click} onImageClick={setModalImage} maxEng={maxEngInResults} />
                                </Grid>
                              ))}
                            </Grid>
                            {totalResults > PAGE_SIZE && (
                              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Pagination count={Math.ceil(totalResults / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} color="primary" size="small" />
                              </Box>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                </>
              )}

              {/* ── Level 3: Specific label - show all images ── */}
              {drillLabel && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {totalResults} images with label "{drillLabel}"
                      {engStats ? ` · Avg engagement: ${formatEng(engStats.mean)} · Median: ${formatEng(engStats.median)}` : ''}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                      <InputLabel>Sort</InputLabel>
                      <Select value={sortBy} label="Sort" onChange={(e: SelectChangeEvent) => setSortBy(e.target.value)}>
                        <MenuItem value="">Default</MenuItem>
                        <MenuItem value="engagement_desc">Engagement (high)</MenuItem>
                        <MenuItem value="engagement_asc">Engagement (low)</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  {resultsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                  ) : (
                    <>
                      <Grid container spacing={2}>
                        {results.map((r) => (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={r._image_name}>
                            <ImageCard result={r} onLabelClick={handleLabelClick} onL1Click={handleL1Click} onL2Click={handleL2Click} onImageClick={setModalImage} maxEng={maxEngInResults} />
                          </Grid>
                        ))}
                      </Grid>
                      {results.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                          <Typography variant="body1" sx={{ color: 'text.secondary' }}>No images found.</Typography>
                        </Box>
                      )}
                      {totalResults > PAGE_SIZE && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                          <Pagination count={Math.ceil(totalResults / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Fallback: no taxonomy, just browse */}
              {!drillL1 && !drillLabel && !taxonomyData && (
                <>
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField size="small" placeholder="Search..." value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      sx={{ minWidth: 280, flex: 1 }}
                    />
                  </Box>
                  {resultsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                  ) : (
                    <Grid container spacing={2}>
                      {results.map((r) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={r._image_name}>
                          <ImageCard result={r} onLabelClick={handleLabelClick} onImageClick={setModalImage} maxEng={maxEngInResults} />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              )}
            </Box>
          )}

        </Box>
      </Box>

      {/* Image detail modal */}
      <ImageDetailModal
        image={modalImage}
        onClose={() => setModalImage(null)}
        onLabelClick={handleLabelClick}
        onL1Click={handleL1Click}
      />
    </ThemeProvider>
  );
};

export default GeminiImageLab;
